import { useEffect, useState } from 'react';
import { Box, Flex, Switch, Text } from '@chakra-ui/react';
import api from '@/utils/api';
import { unwrap } from '@/pages/courses/api';
import { toaster } from '@/components/ui/toaster';
import { CourseReminders } from '@/lib/types';

// Today as a 'YYYY-MM-DD' local calendar date.
function todayWireDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

interface RemindersCardProps {
  courseId: number;
  // An open link has nobody to notify — the toggle disables with a note.
  isOpenAccess: boolean;
}

// The builder's reminders switch, matching the prototype's step-3 card: turn
// reminders on now, tune the cadence on the course's Reminders tab later.
// Enabling here needs a season start (the API refuses an enabled cadence
// without one), so it defaults to today and says so.
export default function RemindersCard({ courseId, isOpenAccess }: RemindersCardProps) {
  const [reminders, setReminders] = useState<CourseReminders | null>(null);
  const [defaultedStart, setDefaultedStart] = useState(false);

  useEffect(() => {
    api
      .get(`courses/${courseId}/reminders`)
      .then((res) => setReminders(unwrap<CourseReminders>(res)))
      .catch(() => setReminders(null));
  }, [courseId]);

  if (!reminders) return null;

  const toggle = async (enabled: boolean) => {
    const seasonStart = reminders.season_start ?? (enabled ? todayWireDate() : '');
    try {
      const res = await api.put(`courses/${courseId}/reminders`, {
        enabled,
        season_start: seasonStart,
        cadence: reminders.cadence || 'weekly',
        due_date: reminders.due_date ?? '',
      });
      setReminders(unwrap<CourseReminders>(res));
      setDefaultedStart(enabled && !reminders.season_start);
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to update reminders',
        type: 'error',
      });
    }
  };

  return (
    <Box
      bg='bg.panel'
      borderWidth='1px'
      borderColor='border'
      borderRadius='lg'
      shadow='sm'
      p={6}
      opacity={isOpenAccess ? 0.55 : 1}
    >
      <Flex align='flex-start' justify='space-between' gap={5}>
        <Box>
          <Text
            fontFamily='heading'
            fontSize='2xs'
            fontWeight='700'
            textTransform='uppercase'
            letterSpacing='0.16em'
            color='fg.muted'
            mb={1}
          >
            Reminders
          </Text>
          <Text fontSize='sm' color='fg.muted' maxW='lg'>
            {isOpenAccess
              ? 'Reminders need log-in access and an assigned audience — an open, anonymous link has nobody to notify.'
              : "Turn reminders on now and set the cadence on the course's Reminders tab — cadence is a course setting, so you can re-time it every season without touching the course itself."}
          </Text>
          {defaultedStart && reminders.enabled && (
            <Text fontSize='xs' color='fg.muted' mt={2}>
              Season start defaulted to today — re-time it on the Reminders tab.
            </Text>
          )}
        </Box>
        <Switch.Root
          checked={reminders.enabled}
          disabled={isOpenAccess}
          onCheckedChange={(e) => toggle(e.checked)}
        >
          <Switch.HiddenInput />
          <Switch.Control />
        </Switch.Root>
      </Flex>
    </Box>
  );
}
